import React from "react";
import JsonLd from "./JsonLd";
import { SITE, absoluteUrl } from "../config/site";
import { servicesCatalog } from "../data/servicesCatalog";

/** Legacy standalone SEO snippet (prefer PageSEO + Helmet for routes). */
export function SEO({ title, description, keywords, canonicalUrl, ogImage, ogType = "website", article, faq }) {
  return (
    <>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {article && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.headline,
            description: article.description,
            author: { "@type": "Organization", name: SITE.name },
            datePublished: article.datePublished,
            dateModified: article.dateModified,
          }}
        />
      )}
      {faq?.length ? (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: { "@type": "Answer", text: item.answer },
            })),
          }}
        />
      ) : null}
    </>
  );
}

export function SoftwareCompanySchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareCompany",
    "@id": `${SITE.url}/#softwarecompany`,
    name: SITE.name,
    alternateName: "Dexton Tech",
    description:
      "Global software engineering partner for SaaS MVP, dedicated remote squads, web & mobile builds, ERP/CRM extensions, and AI integrations—structured proposals and stakeholder-ready demos for clients worldwide.",
    url: SITE.url,
    logo: SITE.ogImage,
    image: SITE.ogImage,
    telephone: SITE.phoneTel,
    email: SITE.email,
    priceRange: "$$–$$$",
    areaServed: ["Worldwide", "United States", "United Kingdom", "Europe", "Asia-Pacific", "Middle East", "India"],
    geo: {
      "@type": "GeoCoordinates",
      latitude: "21.0958",
      longitude: "72.8346",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "20:30",
      },
    ],
    sameAs: Object.values(SITE.social),
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postalCode,
      addressCountry: SITE.address.country,
    },
  };

  return <JsonLd data={schema} />;
}

/** ItemList linking to crawlable service routes */
export function ServicesSchema() {
  const itemListElement = servicesCatalog.map((s, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Service",
      name: s.title,
      description: s.metaDescription,
      url: absoluteUrl(`/services/${s.slug}`),
      provider: {
        "@type": "Organization",
        name: SITE.name,
      },
    },
  }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Technology Services by Dexton Technology",
    description: "Comprehensive engineering services indexed for organic discovery.",
    numberOfItems: itemListElement.length,
    itemListElement,
  };

  return <JsonLd data={schema} />;
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
  return <JsonLd data={schema} />;
}

export default SEO;
